import { createContext, PropsWithChildren, useContext, useState } from 'react'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { noop } from 'lodash'
import { MediaPickerProviderProps, ProcessingFile, ProcessingState, SelectMediaProps } from './Types'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCreateMediaVariantSync } from 'hooks/queries/mediaFiles/useCreateMediaVariant'
import { isImage } from 'components/ImagePickerModal/utils'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { reportError } from 'utils/error'
import { fetchFile } from 'utils/fileDownloadUtils'
import { v4 as uuidv4 } from 'uuid'
import { Provider } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import { useStockVideoDownload } from 'hooks/queries/designer/useStockVideoDownload'
import { isVideo } from 'components/ImagePickerModal/utils'

interface MediaPickerContextType {
  selectedFiles: Array<IMediaFile>
  isFileSelected: (file: IMediaFile) => boolean
  processingFiles: ProcessingFile[]
  handleSelect: (props: SelectMediaProps) => void
  handleUpload: (files: File[]) => void
}

const MediaPickerContext = createContext<MediaPickerContextType>({
  selectedFiles: [],
  isFileSelected: () => false,
  processingFiles: [],
  handleSelect: () => {},
  handleUpload: () => {},
})

export const MediaPickerContextProvider = MediaPickerContext.Provider

export const MediaPickerProvider = ({ children, ...props }: PropsWithChildren<MediaPickerProviderProps>) => {
  const { selectedFiles = [], onSelect = noop, skipBackgroundRemoval = false } = props

  const currentWorkspaceId = useCurrentWorkspaceId()
  const uploadsProjectId = useUploadsProject(currentWorkspaceId).data?.project?.id
  const { uploadImageFilesWithoutLoadingToast } = useImportMediaFiles()
  const { mutate: removeBackgroundMutation } = useCreateMediaVariantSync()
  const [processingFiles, setProcessingFiles] = useState<ProcessingFile[]>([])
  const { downloadHighRes } = useStockVideoDownload()

  const isFileSelected = (file: IMediaFile) => selectedFiles.some((f) => f.id === file.id)

  const upsertProcessingFile = (file: IMediaFile, attrs: Partial<ProcessingFile>) => {
    setProcessingFiles((prev) => {
      const existingFile = prev.find((f) => f.file.id === file.id)
      if (existingFile) {
        return prev.map((f) => (f.file.id === file.id ? { ...f, ...attrs } : f))
      }
      return [...prev, { file, ...attrs }]
    })
  }

  const finalizeFile = async (file: IMediaFile, provider?: Provider) => {
    let updatedFile = file
    if (isImage({ url: file.source_url, type: file.mime_type }) && !skipBackgroundRemoval) {
      if (!file.variants?.some((v) => !!v.transformations.remove_background)) {
        upsertProcessingFile(file, { processingState: ProcessingState.RemovingBackground })
        await removeBackgroundMutation(
          {
            mediaFileId: file.id,
            timeout: 20000,
            transformations: {
              remove_background: { crop: true },
            },
          },
          {
            onError: () => {
              showToastMessage("We weren't able to remove the background of this image.", 'error')
              upsertProcessingFile(file, { processingState: ProcessingState.Complete })
              onSelect(file, false, provider)
            },
            onSuccess: (data) => {
              updatedFile = { ...file, variants: [data] }
              upsertProcessingFile(file, { processingState: ProcessingState.Complete, file: updatedFile })
              onSelect(updatedFile, true, provider)
            },
          },
        )
      } else {
        upsertProcessingFile(file, { processingState: ProcessingState.Complete })
        onSelect(file, true, provider)
      }
    } else {
      upsertProcessingFile(file, { processingState: ProcessingState.Complete })
      onSelect(file, false, provider)
    }
  }

  const uploadFile = async (file: File, mediaFile: IMediaFile) => {
    upsertProcessingFile(mediaFile, { processingState: ProcessingState.Uploading })
    const uploadedFiles = await uploadImageFilesWithoutLoadingToast([file], uploadsProjectId)
    if (!uploadedFiles) return
    const uploadedFile = uploadedFiles[0]
    return uploadedFile as IMediaFile
  }

  const uploadHandler = async (file: File, tempFile: IMediaFile, provider?: Provider) => {
    try {
      const newFile = await uploadFile(file, tempFile)
      if (!newFile) throw new Error('Failed to upload media file')
      const mediaFile = newFile as IMediaFile
      upsertProcessingFile(tempFile, { file: mediaFile })
      finalizeFile(mediaFile, provider)
    } catch (error) {
      showErrorMessageToast('Something went wrong while uploading your media. Please try again.')
      reportError(error)
      upsertProcessingFile(tempFile, { processingState: ProcessingState.Complete })
    }
  }

  const handleUpload = async (files: File[]) => {
    if (files.length === 0) return
    const file = files[0]
    const tempFile = {
      id: uuidv4(),
      title: file.name,
      source_url: URL.createObjectURL(file),
      mime_type: file.type,
      variants: [],
    }
    await uploadHandler(file, tempFile, 'uploads')
  }

  const handleSelect = ({ file, needsDownload, provider }: SelectMediaProps) => {
    if (needsDownload) {
      upsertProcessingFile(file, { processingState: ProcessingState.Downloading })
      
      const downloadAndUpload = async () => {
        try {
          let downloadUrl = file.source_url
          
          const isSelectedVideo = isVideo({
            url: file.source_url,
            type: file.mime_type,
          })
            
          if (isSelectedVideo && file.id) {
            const highResUrl = await downloadHighRes(file.id)
            if (highResUrl) {
              downloadUrl = highResUrl
            }
          }

          const fetchedFile = await fetchFile({ 
            url: downloadUrl, 
            filename: file.title, 
            mimeType: file.mime_type 
          })
          
          return await uploadHandler(fetchedFile, file, provider)
        } catch (error) {
          showErrorMessageToast('Something went wrong while downloading your media. Please try again.')
          reportError(error)
          upsertProcessingFile(file, { processingState: ProcessingState.Complete })
        }
      }
      
      downloadAndUpload()
    } else {
      finalizeFile(file, provider)
    }
  }

  return (
    <MediaPickerContextProvider
      value={{
        selectedFiles,
        isFileSelected,
        processingFiles,
        handleSelect,
        handleUpload,
      }}
    >
      {children}
    </MediaPickerContextProvider>
  )
}

export const useMediaPickerContext = () => {
  return useContext(MediaPickerContext)
}
